(ns accounts.add-account
  (:require
    [reagent.core :as r]
    [oops.core :refer [oget]]
    [very.re-frame.core :as rf :refer [<sub >evt]]
    [css.core :refer [defstyled]]
    [css.form :as form]
    [css.icons :as icon]
    [css.vars :as vars]
    [css.typography :as typo]
    [cljss.core :as cljss]
    [accounts.common :as common]))

(def account-kws #{:name
                   :type
                   :custodian
                   :market-value
                   :account-number
                   :investment
                   :sub-type})

(def kw->name (reduce (fn [m kw] (assoc m kw (name kw))) {} account-kws))

(def name->kw (clojure.set/map-invert kw->name))

(defstyled type-select-s :div
  {:display "grid"
   :margin-bottom "2rem"
   :width "100%"})

(defstyled relative-container :div
  {:position "relative"})

(defn- hidden-field [value]
  [form/input {:value value
               :name (get kw->name :sub-type)
               :type "hidden"}])

(defn- account-type-select
  ([on-select] (account-type-select on-select ""))
  ([on-select default]
   [type-select-s
    [form/label "Account Type"]
    [relative-container
     [form/select {:default-value default
                   :name (get kw->name :type)
                   :required true
                   :on-change #(on-select (.. % -target -value))}
      [:option {:disabled true :value ""} "Select an account type"]
      [:option {:value "INVESTMENT"} "Investment Account"]
      [:option {:value "BANK"} "Bank Account"]
      [:option {:value "CREDIT"} "Credit Card"]
      [:option {:value "LOAN"} "Loan/Mortgage"]
      [:option {:value "PROPERTY"} "Property"]]]]))

(defn- investment-account-type-select
  ([] (investment-account-type-select ""))
  ([default]
   [type-select-s
    [form/label "Investment Account Type"]
    [form/select {:default-value default
                  :name (get kw->name :sub-type)
                  :required true}
     [:option {:disabled true :value ""} "Select an investment account type"]
     [:option {:value "FOURZEROONEK"} "401k"]
     [:option {:value "IRA"} "IRA"]
     [:option {:value "FOURZEROTHREEB"} "403B"]
     [:option {:value "OTHER"} "Other"]]]))

(defn- bank-account-type-select
  ([] (bank-account-type-select ""))
  ([default]
   [type-select-s
    [form/label "Bank Account Type"]
    [form/select {:default-value default
                  :name (get kw->name :sub-type)
                  :required true}
     [:option {:disabled true :value ""} "Select a bank account type"]
     [:option {:value "CHECKING"} "Checking"]
     [:option {:value "SAVINGS"} "Savings"]
     [:option {:value "OTHER"} "Other"]]]))

(defn- loan-type-select
  ([] (loan-type-select ""))
  ([default]
   [type-select-s
    [form/label "Loan Type"]
    [form/select {:default-value default
                  :name (get kw->name :sub-type)
                  :required true}
     [:option {:disabled true :value ""} "Select a loan type"]
     [:option {:value "CAR"} "Car"]
     [:option {:value "MORTGAGE"} "Mortgage"]
     [:option {:value "LINEOFCREDIT"} "Line of Credit"]
     [:option {:value "OTHER"} "Other"]]]))

(defstyled select-account-type-s :div
  {:align-items "center"
   :display "flex"
   :flex-direction "column"
   :width "100%"
   :max-width "330px"
   :margin "0 auto"})

(defn- select-account-type [on-select]
  [common/modal-with-header {:title "Add an account manually"
                             :on-cancel #(>evt [:abort-add-account])}
   [select-account-type-s
    [account-type-select on-select]]])

(defstyled generic-form-group-s :div
  {:display "grid"
   :margin-bottom "2rem"
   :position "relative"
   :&:before {:content "$"
              :position "absolute"
              :display (with-meta #(if % "block" "none") :currency?)
              :color vars/gray
              :left "10px"
              :top "43px"}})

(defstyled generic-form-container-s :div
  {:display "flex"
   :flex-direction "column"
   :padding-bottom "2.5rem"
   :width "100%"
   :max-width "330px"
   :margin "0 auto 2rem"})

(defstyled generic-account-form-s :form
  {:align-items "center"
   :display "flex"
   :flex-direction "column"})

(defn- generic-account-form []
  (let [form-id (str "id-" (random-uuid))
        gather-form-data (fn [id]
                           (let [inputs (js->clj
                                          (.map (.from js/Array
                                                       (.getElementsByTagName (.getElementById js/document id) "INPUT"))
                                                (fn [x] #js [(oget x "name") (oget x "value")])))
                                 selects (js->clj
                                           (.map (.from js/Array
                                                        (.getElementsByTagName (.getElementById js/document id) "SELECT"))
                                                 (fn [x] #js [(oget x "name") (oget x "value")])))]
                             (reduce (fn [m [k v]] (assoc m (get name->kw k) v)) {} (concat inputs selects))))]
    (fn [{:keys [fields on-submit button default-values]}]
      [generic-account-form-s
       {:id form-id
        :auto-complete "off"
        :on-submit (fn [evt]
                     (.preventDefault evt)
                     (on-submit (gather-form-data form-id)))}
       [generic-form-container-s
        (for [{:keys [label renderer name type currency? unit?] :as props} fields]
          (if renderer
            ;; if a renderer is provided just defer
            (with-meta renderer {:key label})
            ;; if a renderer isnt provide default to text input (for now)
            (with-meta
              [generic-form-group-s {:currency? currency?}
               [form/label label]
               [form/input (merge props
                                  {:type (or type "text")
                                   :required true
                                   :unit? unit?
                                   :default-value (get default-values name "")
                                   :name (get kw->name name)})]]
              {:key label})))
        [form/button {:type "submit"} button]]])))

(defstyled form-error-s :p
  {:color vars/red
   :height "2rem"
   :overflow "hidden"
   :text-overflow "ellipsis"
   :padding "0 2rem 1rem 2rem"
   :text-align "center"
   :display (with-meta #(if % "block" "none") :visible?)})

(defn add-account-form []
  (let [form (rf/subscribe [:accounts/form])]
    (fn [{:keys [fields on-submit]}]
      [common/modal-with-header {:title "Add an account manually"
                                 :on-cancel #(>evt [:abort-add-account])}
       [:div
        [form-error-s {:visible? (:error @form)} (:message @form)]
        [generic-account-form {:fields fields
                               :disabled (:submitting? @form)
                               :on-submit (fn [& args]
                                            (when-not (:submitting? @form)
                                                      (apply on-submit args)))
                               :button (if (:submitting? @form) "Adding Account..." "Add Account")}]]])))

(defstyled add-account-s :div
  {:display "grid"
   :grid-column-gap "2rem"
   :grid-template-columns "minmax(1rem, 1fr) 3fr 3fr minmax(1rem, 1fr)"
   :overflow "auto"
   ::cljss/media {[[:max-width "768px"]]
                  {:display "block"
                   :padding "0 1rem 2rem"}}})

(defstyled add-account-section :section
  {:display "flex"
   :flex-direction "column"
   :text-align "center"
   :&:first-child {:grid-column "2"}
   :&:last-child {:grid-column "3"}
   ::cljss/media {[[:max-width "768px"]]
                  {:&:first-child {:border-bottom "1px solid #AFAFAF"
                                   :padding-bottom "3rem"
                                   :margin-bottom "1rem"}}}})

(defstyled i-s :i
  {:font-size "3rem"
   :margin "2rem 0"})

(defstyled add-description :p
  {:color vars/gray
   :line-height "24px"
   :max-width "330px"
   :margin "1.5rem auto 0"})

(defstyled account-modal-s :div
  {:display "grid"
   :min-height "30rem"
   :max-height "90vh"
   :width "45rem"
   :overflow "auto"
   ::cljss/media {[[:max-width "768px"]]
                  {:width "100vw"
                   :height "100vh"
                   :max-height "100%"}}})

(defn account-type-form
  "We really dont want to have to copy paste this guy everywhere with slight
  tweaks to handle setting, editing, etc. Instead we'll reuse it. Consumers
  just have to provide a type (one of the strings in the set below), an
  on-submit fn (invoked on form submit), and a form -- which should be a fn
  that, given an account-type, some fields, and an on-submit, will render any
  content it so chooses (headers, footers, etc.) but ultimately calls down to
  generic-account-form and hands over the type, form, and on-submit fn. This is
  slightly convoluted, but it gives us a way to render the same in different
  contexts, within different containers, and invoke different callbacks."
  [{:keys [form type on-submit default-values]}]
  #_#{"INVESTMENT"
      "BANK"
      "CREDIT"
      "LOAN"
      "PROPERTY"}
  (let [state (r/atom type)]
    (fn []
      (condp = @state
        "INVESTMENT"
        [form {:account-type "INVESTMENT"
               :fields [{:label "Account Type"
                         :name :type
                         :renderer [account-type-select #(reset! state %) "INVESTMENT"]}
                        {:label "Account description/name"
                         :name :name}
                        {:label "Financial institution"
                         :name :custodian}
                        {:label "Account Number (Last 4 Digits)"
                         :name :account-number
                         :type "tel"
                         :max-length "4"}
                        {:label "Market Value"
                         :name :market-value
                         :unit? true
                         :currency? true
                         :type "number"}
                        {:label "Investment Account Type"
                         :renderer [investment-account-type-select (:sub-type default-values)]
                         :name :sub-type}]
               :default-values default-values
               :on-submit on-submit}]

        "BANK"
        [form {:account-type "BANK"
               :fields [{:label "Account Type"
                         :renderer [account-type-select #(reset! state %) "BANK"]
                         :name :type}
                        {:label "Account description/name"
                         :name :name}
                        {:label "Financial institution"
                         :name :custodian}
                        {:label "Account Number (Last 4 Digits)"
                         :name :account-number
                         :type "tel"
                         :max-length "4"}
                        {:label "Current Balance"
                         :name :market-value
                         :unit? true
                         :currency? true
                         :type "number"}
                        {:label "Bank Type"
                         :renderer [bank-account-type-select (:sub-type default-values)]
                         :name :sub-type}]
               :default-values default-values
               :on-submit on-submit}]

        "CREDIT"
        [form {:account-type "CREDIT"
               :fields [{:label "Account Type"
                         :name :type
                         :renderer [account-type-select #(reset! state %) "CREDIT"]}
                        {:label "Account description/name"
                         :name :name}
                        {:label "Financial institution"
                         :name :custodian}
                        {:label "Account Number (Last 4 Digits)"
                         :name :account-number
                         :type "tel"}
                        {:label "Current Balance"
                         :type "number"
                         :unit? true
                         :currency? true
                         :name :market-value}
                        {:renderer [hidden-field "CREDIT"]
                         :label "credit"
                         :name :sub-type}]
               :default-values default-values
               :on-submit on-submit}]

        "LOAN"
        [form {:account-type "LOAN"
               :fields [{:label "Account Type"
                         :name :type
                         :renderer [account-type-select #(reset! state %) "LOAN"]}
                        {:label "Account description/name"
                         :name :name}
                        {:label "Financial institution"
                         :name :custodian}
                        {:label "Account Number (Last 4 Digits)"
                         :name :account-number
                         :type "tel"
                         :max-length "4"}
                        {:label "Current Balance"
                         :name :market-value
                         :unit? true
                         :currency? true
                         :type "number"}
                        {:label "Loan Type" :renderer [loan-type-select (:sub-type default-values)]
                         :name :sub-type}]
               :default-values default-values
               :on-submit on-submit}]

        "PROPERTY"
        [form {:account-type "PROPERTY"
               :fields [{:label "Account Type"
                         :name :type
                         :renderer [account-type-select #(reset! state %) "PROPERTY"]}
                        {:label "Account description/name"
                         :name :name}
                        {:label "Financial institution"
                         :name :custodian}
                        {:label "Current Value"
                         :name :market-value
                         :unit? true
                         :currency? true
                         :type "number"}
                        {:renderer [hidden-field "PROPERTY"]
                         :label "property"
                         :name :sub-type}]
               :default-values default-values
               :on-submit on-submit}]))))

(defn ^:export add-account-modal []
  #_#{:display-options
      :select-manual-account-type}
      ;; or one of the "ACCOUNT_TYPE" strings that will render a select account
      ;; type form, represented in our condp default case

  (let [state (r/atom :display-options)
        on-submit #(>evt [:add-external-account %])]
    (fn []
      [account-modal-s
       (condp = @state
         :display-options
         [common/modal-with-header {:title "Add an account"
                                    :on-cancel #(>evt [:abort-add-account])}
          [add-account-s
           [add-account-section
            [i-s [icon/link]]
            [form/button {:on-click #(rf/>evt [:connect-account])} "Connect your accounts"]
            [add-description "Log into your financial institutions to add your accounts."]]
           [add-account-section
            [i-s [icon/accounts]]
            [form/button {:on-click #(reset! state :select-manual-account-type)} "Add manually"]
            [add-description "Connect accounts manually without logging into a financial institution."]]]]

         :select-manual-account-type
         [select-account-type #(reset! state %)]

         ;; for all others states, use our type-form
         [account-type-form {:form add-account-form
                             :type @state
                             :on-submit on-submit}])])))
